import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Text,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Tooltip,
  Badge,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  VStack,
} from '@chakra-ui/react'
import { FiPlus, FiEdit2, FiEye, FiSearch, FiUsers, FiCreditCard, FiTrash2 } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useAdminGuard } from '@/hooks/useAdminGuard'
import { Organization } from '@/types'
import { BaseDataTable, PageHeader, StatusBadge } from '@/components/Common'
import DeleteOrganizationDialog from '@/components/Admin/DeleteOrganizationDialog'
import type { TableColumn } from '@/components/Common'
import { useOrganizations } from '@/hooks'
import { useAppTranslation } from '@/contexts/LanguageContext'

export default function AdminOrganizationsPage() {
  const { isAuthorized, ForbiddenView } = useAdminGuard()
  const navigate = useNavigate()
  const { organizations, loading, error, refreshOrganizations, stats } = useOrganizations()
  const [searchQuery, setSearchQuery] = useState('')
  const [organizationToDelete, setOrganizationToDelete] = useState<Organization | null>(null)
  const { t } = useAppTranslation('admin')

  React.useEffect(() => {
    const handleFocus = () => {
      refreshOrganizations()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [refreshOrganizations])

  const handleEdit = useCallback(
    (organization: Organization) => {
      navigate(`/admin/organizations/${organization.id}/edit`)
    },
    [navigate]
  )

  const handleView = useCallback(
    (organization: Organization) => {
      navigate(`/admin/organizations/${organization.id}`)
    },
    [navigate]
  )

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }, [])

  const filteredOrganizations = useMemo(() => {
    if (!searchQuery) return organizations

    const query = searchQuery.toLowerCase()
    return organizations.filter(
      (org) =>
        org.name.toLowerCase().includes(query) ||
        org.slug.toLowerCase().includes(query) ||
        org.description?.toLowerCase().includes(query) ||
        org.plan?.name.toLowerCase().includes(query)
    )
  }, [organizations, searchQuery])

  const columns: TableColumn<Organization>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('organizationManagement.table.name'),
        accessor: (org) => (
          <Box>
            <Text fontWeight="medium" color="gray.800">
              {org.name}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {org.slug}
            </Text>
          </Box>
        ),
      },
      {
        key: 'plan',
        header: t('organizationManagement.table.plan'),
        hideOnMobile: true,
        accessor: (org) => (
          <Box>
            {org.plan ? (
              <HStack spacing={2}>
                <Text fontWeight="medium" color="gray.800" fontSize="sm">
                  {org.plan.name}
                </Text>
                <Badge
                  colorScheme={org.plan.type === 'preset' ? 'blue' : 'purple'}
                  size="sm"
                  variant="subtle"
                >
                  {org.plan.type}
                </Badge>
              </HStack>
            ) : (
              <Text fontSize="sm" color="gray.500">
                {t('organizationManagement.table.noPlanAssigned')}
              </Text>
            )}
          </Box>
        ),
      },
      {
        key: 'users_count',
        header: t('organizationManagement.table.users'),
        hideOnMobile: true,
        accessor: (org) => (
          <HStack spacing={1}>
            <FiUsers size={14} color="gray.500" />
            <Text fontSize="sm" color="gray.600">
              {org.users?.length || 0}
            </Text>
          </HStack>
        ),
      },
      {
        key: 'active',
        header: t('organizationManagement.table.status'),
        accessor: (org) => <StatusBadge status={org.active ? 'active' : 'inactive'} />,
      },
      {
        key: 'created_at',
        header: t('organizationManagement.table.created'),
        hideOnMobile: true,
        accessor: (org) => (
          <Text fontSize="sm" color="gray.600">
            {formatDate(org.created_at)}
          </Text>
        ),
      },
      {
        key: 'actions',
        header: t('organizationManagement.table.actions'),
        width: '120px',
        align: 'center' as const,
        accessor: (org) => (
          <HStack spacing={1} justify="center">
            <Tooltip label={t('organizationManagement.table.viewDetails')} placement="top">
              <IconButton
                aria-label={t('organizationManagement.table.viewOrganization')}
                icon={<FiEye />}
                size="sm"
                variant="ghost"
                onClick={() => handleView(org)}
                _hover={{ bg: 'blue.50', color: 'blue.600' }}
              />
            </Tooltip>
            <Tooltip label={t('organizationManagement.table.editOrganization')} placement="top">
              <IconButton
                aria-label={t('organizationManagement.table.editOrganization')}
                icon={<FiEdit2 />}
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  handleEdit(org)
                }}
                _hover={{ bg: 'blue.50', color: 'blue.600' }}
              />
            </Tooltip>
            <Tooltip label={t('deleteOrganization.action')} placement="top">
              <IconButton
                aria-label={t('deleteOrganization.action')}
                icon={<FiTrash2 />}
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  setOrganizationToDelete(org)
                }}
                _hover={{ bg: 'red.50', color: 'red.600' }}
              />
            </Tooltip>
          </HStack>
        ),
      },
    ],
    [formatDate, handleEdit, handleView, t]
  )

  if (!isAuthorized) return ForbiddenView

  if (error && !loading) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    )
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={8} align="stretch">
        <PageHeader
          title={t('organizationManagement.title')}
          description={t('organizationManagement.description')}
          actions={[
            {
              label: t('organizationManagement.createOrganization'),
              icon: <FiPlus />,
              onClick: () => navigate('/admin/organizations/create'),
            },
          ]}
        >
          <Box mt={4}>
            <InputGroup maxW="400px">
              <InputLeftElement>
                <FiSearch color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder={t('organizationManagement.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="white"
                borderRadius="lg"
                size="md"
              />
            </InputGroup>
          </Box>
        </PageHeader>

        {!loading && organizations.length > 0 && (
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
            <Stat bg="blue.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
              <StatLabel color="blue.600">
                <HStack>
                  <Icon as={FiUsers} />
                  <Text>{t('organizationManagement.stats.totalOrganizations')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="blue.800" fontSize="2xl">
                {stats.total}
              </StatNumber>
              <StatHelpText>{t('organizationManagement.stats.allOrganizations')}</StatHelpText>
            </Stat>

            <Stat bg="green.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.200">
              <StatLabel color="green.600">
                <HStack>
                  <Icon as={FiUsers} />
                  <Text>{t('organizationManagement.stats.active')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="green.800" fontSize="2xl">
                {stats.active}
              </StatNumber>
              <StatHelpText>{t('organizationManagement.stats.activeOrganizations')}</StatHelpText>
            </Stat>

            <Stat bg="orange.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="orange.200">
              <StatLabel color="orange.600">
                <HStack>
                  <Icon as={FiUsers} />
                  <Text>{t('organizationManagement.stats.inactive')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="orange.800" fontSize="2xl">
                {stats.inactive}
              </StatNumber>
              <StatHelpText>
                {t('organizationManagement.stats.inactiveOrganizations')}
              </StatHelpText>
            </Stat>

            <Stat bg="purple.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="purple.200">
              <StatLabel color="purple.600">
                <HStack>
                  <Icon as={FiCreditCard} />
                  <Text>{t('organizationManagement.stats.withPlans')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="purple.800" fontSize="2xl">
                {stats.withPlans}
              </StatNumber>
              <StatHelpText>
                {t('organizationManagement.stats.organizationsWithPlans')}
              </StatHelpText>
            </Stat>

            <Stat bg="cyan.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="cyan.200">
              <StatLabel color="cyan.600">
                <HStack>
                  <Icon as={FiUsers} />
                  <Text>{t('organizationManagement.stats.totalUsers')}</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="cyan.800" fontSize="2xl">
                {stats.totalUsers}
              </StatNumber>
              <StatHelpText>
                {t('organizationManagement.stats.acrossAllOrganizations')}
              </StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        <BaseDataTable
          columns={columns}
          data={filteredOrganizations}
          loading={loading}
          emptyMessage={t('organizationManagement.empty.noOrganizations')}
          emptyDescription={
            searchQuery
              ? t('organizationManagement.empty.noMatchSearch', { query: searchQuery })
              : t('organizationManagement.empty.willAppearOnceCreated')
          }
          emptyIcon={<FiUsers />}
          getRowKey={(org) => org.id}
          animateRows={true}
          enableHover={true}
          onRowClick={(org) => handleView(org)}
        />
      </VStack>

      {organizationToDelete && (
        <DeleteOrganizationDialog
          isOpen={!!organizationToDelete}
          onClose={() => setOrganizationToDelete(null)}
          organization={organizationToDelete}
          onDeleted={() => {
            setOrganizationToDelete(null)
            refreshOrganizations()
          }}
        />
      )}
    </Box>
  )
}
